import { useIntl } from 'react-intl';
import { useQuery, UseQueryResult } from 'react-query';
import { HeaderData } from 'adapters/types';
import { useClient } from 'components/ClientProvider';
import {
  getCompanyId,
  getDepartmentName,
  getStoreId
} from 'components/StoreSelect/slice';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import { IError, onErrorHandler } from 'hooks/helper';
import { messages } from 'pages/VendorsTablePage/components/PrintChecks/messages';
import {
  getHighVolumeColumnState,
  getHighVolumeFilterData,
  setSelectAllSelectedInvoices
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import {
  VendorsResponseDataType,
  VendorsTableDataType
} from 'pages/VendorsTablePage/components/VendorsTable/types';
import { errors } from 'pages/VendorsTablePage/messages';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  SELECT_ALL_CHECKS_PAGE_NO,
  SELECT_ALL_INVOICES_PAGE_SIZE,
  SELECT_ALL_INVOICES_MAX_COUNT,
  SORT_COLUMN_PARAMS,
  SORT_ORDER_PARAMS
} from '../constants';
import { VendorsTableSelectAllType } from '../types';
import { getAllSelectableInvoices } from '../utils';

export const useSelectAllInvoices = ({
  isSelectAll
}: VendorsTableSelectAllType): UseQueryResult<
  VendorsResponseDataType,
  IError
> => {
  const intl = useIntl();
  const client = useClient();
  const dispatch = useAppDispatch();
  const storeId = useAppSelector(getStoreId);
  const companyId = useAppSelector(getCompanyId);
  const filterData = useAppSelector(getHighVolumeFilterData);
  const sortColumnData = useAppSelector(getHighVolumeColumnState);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const sortColumn = sortColumnData
    ? SORT_COLUMN_PARAMS[sortColumnData.colId]
    : undefined;
  const sortOrder = sortColumnData
    ? SORT_ORDER_PARAMS[sortColumnData.sort]
    : undefined;

  return useQuery(
    ['selectAllInvoices', headers, storeId, isSelectAll],
    () =>
      client?.data.fetchHighVolumeVendors(
        headers,
        companyId,
        storeId,
        SELECT_ALL_CHECKS_PAGE_NO,
        SELECT_ALL_INVOICES_PAGE_SIZE,
        filterData?.dueDate,
        filterData?.paymentMethod,
        filterData?.selectedTab,
        filterData?.debouncedSearchValue,
        sortColumn,
        sortOrder
      ),
    {
      onSuccess: data => {
        const rowDataByVendor: VendorsTableDataType[] =
          data?.vendors
            .map(vendorData => ({
              ...vendorData,
              id: vendorData.vendorNo,
              amount: vendorData.totalAmount,
              discountAmount: vendorData.totalDiscountAmount,
              netAmount: vendorData.totalNetAmount,
              onHold: vendorData.onHold ?? false,
              vendorOnHold: vendorData.onHold ?? false,
              invoiceDescription: '',
              invoiceId: ''
            }))
            .filter(invoice => !invoice.onHold && !invoice?.vendorOnHold) ?? [];
        const selectableInvoices = getAllSelectableInvoices(rowDataByVendor);
        dispatch(
          setSelectAllSelectedInvoices(
            selectableInvoices?.length > SELECT_ALL_INVOICES_MAX_COUNT
              ? selectableInvoices.slice(0, SELECT_ALL_INVOICES_MAX_COUNT)
              : selectableInvoices
          )
        );

        if (selectableInvoices?.length > SELECT_ALL_INVOICES_MAX_COUNT) {
          dispatch(
            setSuccessMessage(
              intl.formatMessage(messages.bulkInvoicesAuthorizing, {
                maxInvoice: SELECT_ALL_INVOICES_MAX_COUNT
              })
            )
          );
        }
      },
      enabled: isSelectAll,
      staleTime: 0,
      onError: (error: IError) =>
        onErrorHandler(
          error,
          dispatch,
          intl,
          intl.formatMessage(errors.vendorApiFailureMsg)
        )
    }
  );
};
